import type { AxiosError } from 'axios';
import { ApiError, isApiError } from './types';

function inferCodeFromStatus(status: number): string {
  if (status === 400) return 'common.badRequest';
  if (status === 401) return 'common.unauthorized';
  if (status === 403) return 'common.forbidden';
  if (status === 404) return 'common.notFound';
  if (status === 409) return 'common.conflict';
  // why: nginx and the Azure front end reject oversized uploads with a 413 HTML page before
  // the API can answer with its own code.
  if (status === 413) return 'common.requestTooLarge';
  if (status === 501) return 'common.notImplemented';
  if (status >= 500) return 'common.internal';
  return 'common.badRequest';
}

function synthesize(status: number, title?: string, detail?: string | null): ApiError {
  return {
    title: title || 'Error',
    status,
    code: inferCodeFromStatus(status),
    detail: detail ?? null,
  };
}

// Normalize an axios error (or any thrown error from the API layer) into an ApiError.
// Backwards-compatible: if the response body already follows the new contract, it is
// returned as-is. Otherwise we synthesize a minimal ApiError from the HTTP status.
export function parseAxiosError(error: unknown): ApiError {
  // Fast path: the response interceptor in custom-instance.ts attaches a
  // pre-parsed ApiError to each AxiosError it rejects. Check this BEFORE
  // isApiError(error), because AxiosError itself has `code` + `status` and
  // would otherwise pass a loose structural check.
  const enriched = error as { apiError?: unknown } | undefined;
  if (enriched && isApiError(enriched.apiError)) {
    return enriched.apiError;
  }

  if (isApiError(error)) return error;

  const axiosError = error as AxiosError | undefined;
  const response = axiosError?.response;

  if (!response) {
    // Network error, request cancelled, or non-axios error.
    return {
      title: axiosError?.message || 'Network error',
      status: 0,
      code: 'common.internal',
    };
  }

  const body = response.data;
  if (isApiError(body)) {
    return body;
  }

  // Older endpoints may still return { error: "..." } or RFC 7807 without `code`.
  const legacyDetail =
    (body && typeof body === 'object'
      ? ((body as { detail?: string; error?: string; title?: string }).detail ??
        (body as { error?: string }).error ??
        (body as { title?: string }).title)
      : undefined) ?? response.statusText;

  return synthesize(response.status, response.statusText, legacyDetail);
}
